// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — types d'aide d'un dispositif AIDE (fiche du dispositif).
// La LISTE des types est une donnée décidée par le bureau : aucun type n'est pré-rempli. Circuit du serveur :
// préparer (ds_dispositif_preparer) → valider par une AUTRE personne (ds_dispositif_valider) → actif ;
// désactiver (ds_dispositif_suspendre, motif), définitif. Un type validé est figé : un changement = un nouveau type.
// Les VALEURS (forfait, barème, plafond par cas, justificatifs complémentaires, sensibilité renforcée) se règlent au
// Centre de configuration (portée « type d'aide ») : cet écran les affiche, il ne les saisit pas.
// Dispositif de démonstration : code DEMO_…, libellé « [DÉMO] … » (exigé par le serveur).
// Routes : GET/POST /api/dispositifs/aides/dispositifs/:id/types, POST /types/:id/valider, /types/:id/desactiver.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, CheckCircle2, ListPlus, Settings2, ShieldAlert } from 'lucide-react';
import { Badge, Bouton, BandeauInfo } from '../../design/composants';
import { toArray, libelle } from './api';
import { Champ, Etat } from './commun';
import { francs } from './apiSocial';
import { useLectureSociale, EtatsLecture, FenetreActe, TitreSection, MarqueDemo, ChampMotif, motifValide } from './communSocial';
import { lienCentre } from './communCampagnes';
import { useMiseEnPlace, FORMES_AIDE, AYANTS_DROIT, PIECES_AIDE, ETATS_TYPE_AIDE, CIRCUITS_AIDE } from './apiMiseEnPlace';

const CODE_RE = /^[A-Z][A-Z0-9_]{2,31}$/;

/** Valeur du règlement d'un type (lue au Centre de configuration) ; absente → mention, jamais de valeur inventée. */
function ValeurReglement({ t }) {
  const r = t.reglement || {};
  const lignes = [];
  if (r.montant_forfait !== null && r.montant_forfait !== undefined) lignes.push(`Forfait ${francs(r.montant_forfait)}`);
  if (toArray(r.bareme).length) lignes.push(`Barème : ${toArray(r.bareme).map((p) => `dès ${francs(p.seuil)} → ${francs(p.valeur)}`).join(' ; ')}`);
  if (r.plafond_cas !== null && r.plafond_cas !== undefined) lignes.push(`Plafond par cas ${francs(r.plafond_cas)}`);
  if (r.circuit) lignes.push(libelle(CIRCUITS_AIDE, r.circuit));
  return (
    <>
      {lignes.length ? lignes.map((l) => <span key={l} style={{ display: 'block' }}>{l}</span>) : <span className="rh-cellule-secondaire">Aucune valeur en vigueur</span>}
      {toArray(r.absentes).length > 0 && <span style={{ display: 'block', marginTop: 4 }}><Badge ton="alerte">{toArray(r.absentes).length} règle(s) absente(s)</Badge></span>}
    </>
  );
}

function FenetrePreparerType({ dispositif, onFermer, onFait }) {
  const api = useMiseEnPlace();
  const demo = dispositif.donnees_demo === true;
  const [code, setCode] = useState(demo ? 'DEMO_' : '');
  const [lib, setLib] = useState(demo ? '[DÉMO] ' : '');
  const [forme, setForme] = useState('FORFAIT');
  const [ayant, setAyant] = useState('AUCUN');
  const [pieces, setPieces] = useState([]);
  const [sensible, setSensible] = useState(false);
  const c = code.trim().toUpperCase();
  const l = lib.trim();
  const marqueOk = demo ? (c.startsWith('DEMO_') && l.startsWith('[DÉMO]') && l.length > '[DÉMO]'.length + 1) : (!c.startsWith('DEMO_') && !l.startsWith('[DÉMO]'));
  const pret = CODE_RE.test(c) && l.length >= 2 && l.length <= 120 && marqueOk;
  const basculer = (p) => setPieces((x) => (x.includes(p) ? x.filter((y) => y !== p) : [...x, p]));
  return (
    <FenetreActe titre="Préparer un type d'aide" Icone={ListPlus} libelleBouton="Préparer le type" pret={pret} onFermer={onFermer} large
      aide="Le type est créé inactif ; une autre personne habilitée le valide. Les montants, le plafond et les justificatifs complémentaires se règlent ensuite au Centre de configuration."
      onConfirmer={async () => {
        await api.preparerTypeAide(dispositif.id, { code: c, libelle: l, forme, ayant_droit: ayant, pieces_exigees: pieces, sensible });
        onFait(`Type « ${l} » préparé : il attend la validation d'une autre personne.`);
      }}>
      {demo && <BandeauInfo ton="alerte" titre="Dispositif de démonstration">Le code commence par DEMO_ et le libellé par « [DÉMO] ». Un type de démonstration n'est admis qu'en base de test ou de recette.</BandeauInfo>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Champ libelle="Code" requis aide="3 à 32 caractères : majuscules, chiffres, _.">
          <input className="rh-champ-saisie" maxLength={32} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </Champ>
        <Champ libelle="Libellé" requis aide={marqueOk || !l ? '2 à 120 caractères.' : (demo ? 'Le libellé commence par « [DÉMO] ».' : 'Un type réel ne porte pas la marque [DÉMO].')}>
          <input className="rh-champ-saisie" maxLength={120} value={lib} onChange={(e) => setLib(e.target.value)} />
        </Champ>
        <Champ libelle="Forme" requis>
          <select className="rh-champ-saisie" value={forme} onChange={(e) => setForme(e.target.value)}>
            {Object.entries(FORMES_AIDE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Champ>
        <Champ libelle="Bénéficiaire de l'événement">
          <select className="rh-champ-saisie" value={ayant} onChange={(e) => setAyant(e.target.value)}>
            {Object.entries(AYANTS_DROIT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Champ>
      </div>
      <fieldset style={{ border: '1px solid var(--sg-bord)', borderRadius: 6, padding: '8px 12px', margin: '0 0 12px' }}>
        <legend style={{ fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', padding: '0 4px' }}>Justificatifs requis au dépôt</legend>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {Object.entries(PIECES_AIDE).map(([k, v]) => (
            <label key={k} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
              <input type="checkbox" checked={pieces.includes(k)} onChange={() => basculer(k)} />{v}
            </label>
          ))}
        </div>
      </fieldset>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, marginBottom: 12 }}>
        <input type="checkbox" checked={sensible} onChange={(e) => setSensible(e.target.checked)} style={{ marginTop: 3 }} />
        <span>Type sensible (santé, décès…) : motif et pièces réservés aux personnes habilitées, lectures tracées, nom masqué dans les listes.</span>
      </label>
    </FenetreActe>
  );
}

function FenetreValiderType({ type, onFermer, onFait }) {
  const api = useMiseEnPlace();
  return (
    <FenetreActe titre={`Valider le type « ${type.libelle} »`} Icone={CheckCircle2} libelleBouton="Valider et activer" onFermer={onFermer}
      aide="Le type devient proposable au guichet. La personne qui l'a préparé ne le valide pas (double regard). Un type validé ne se modifie plus."
      onConfirmer={async () => { await api.validerTypeAide(type.id); onFait(`Type « ${type.libelle} » validé : il est actif.`); }}>
      <p style={{ margin: 0, fontSize: 14 }}>{type.code} · {libelle(FORMES_AIDE, type.forme)} · {libelle(AYANTS_DROIT, type.ayant_droit)}{type.sensible ? ' · sensible' : ''}</p>
    </FenetreActe>
  );
}

function FenetreDesactiverType({ type, onFermer, onFait }) {
  const api = useMiseEnPlace();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`Désactiver le type « ${type.libelle} »`} Icone={Ban} libelleBouton="Désactiver" danger pret={motifValide(motif)} onFermer={onFermer}
      aide="Définitif : le type n'est plus proposé au guichet ; les demandes déjà déposées gardent leurs règles figées."
      onConfirmer={async () => { await api.desactiverTypeAide(type.id, motif.trim()); onFait(`Type « ${type.libelle} » désactivé.`); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

export default function TypesAide({ dispositif, droits }) {
  const api = useMiseEnPlace();
  const liste = useLectureSociale(() => api.typesAide(dispositif.id), [api, dispositif.id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const types = toArray(liste.donnees);
  const peutPreparer = droits.a('ds_dispositif_preparer') && dispositif.etat !== 'ARCHIVE';
  const fait = (m) => { setFenetre(null); setInfo(m); liste.recharger(); };
  return (
    <section className="rh-carte">
      <TitreSection action={peutPreparer ? <Bouton petit variante="primaire" Icone={ListPlus} onClick={() => { setInfo(''); setFenetre({ type: 'preparer' }); }}>Préparer un type</Bouton> : null}>
        Types d'aide
      </TitreSection>
      <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>
        La liste des types est décidée par le bureau : aucun type n'est fourni d'avance. Montant, barème, plafond et justificatifs complémentaires se règlent au{' '}
        <Link className="rh-lien" to={lienCentre({ organisation: dispositif.organisation_id, dispositif: dispositif.id })} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Settings2 size={13} aria-hidden="true" />Centre de configuration</Link>
        {' '}(portée « type d'aide »), préparés puis validés par deux personnes.
      </p>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <EtatsLecture lecture={liste} Icone={ListPlus} estVide={!types.length} titreVide="Aucun type d'aide"
        vide={peutPreparer ? 'Préparez les types décidés par le bureau ; chacun sera validé par une autre personne.' : 'Les types d\'aide sont préparés par le Secrétaire général et validés par le Président.'}>
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Type</th><th>Forme</th><th>Justificatifs</th><th>Règlement en vigueur</th><th>État</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {types.map((t) => {
                const pieces = [...new Set([...toArray(t.pieces_exigees), ...toArray(t.reglement?.pieces_exigees)])];
                return (
                  <tr key={t.id}>
                    <td>
                      <span className="rh-cellule-principale">{t.libelle}</span> <MarqueDemo actif={t.donnees_demo} />
                      {(t.sensible || t.reglement?.sensible) && <> <Badge ton="danger" title="Motif et pièces réservés, lectures tracées"><ShieldAlert size={12} aria-hidden="true" /> Sensible</Badge></>}
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{t.code} · {libelle(AYANTS_DROIT, t.ayant_droit)}</span>
                    </td>
                    <td>{libelle(FORMES_AIDE, t.forme)}</td>
                    <td>{pieces.length ? pieces.map((p) => libelle(PIECES_AIDE, p)).join(', ') : <span className="rh-cellule-secondaire">Aucun</span>}</td>
                    <td>
                      <ValeurReglement t={t} />
                      <Link className="rh-lien" style={{ fontSize: 13 }} to={lienCentre({ organisation: dispositif.organisation_id, dispositif: dispositif.id, portee: 'TYPE_AIDE', objet: t.id })}>Règlement du type</Link>
                    </td>
                    <td><Etat table={ETATS_TYPE_AIDE} code={t.etat} />{t.motif_desactivation && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{t.motif_desactivation}</span>}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {t.etat === 'A_VALIDER' && droits.a('ds_dispositif_valider') && <Bouton petit variante="primaire" onClick={() => { setInfo(''); setFenetre({ type: 'valider', t }); }}>Valider</Bouton>}
                      {t.etat !== 'DESACTIVE' && droits.a('ds_dispositif_suspendre') && <Bouton petit variante="discret" onClick={() => { setInfo(''); setFenetre({ type: 'desactiver', t }); }}>Désactiver</Bouton>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="rh-form-aide" style={{ margin: '8px 0 0' }}>Un type validé ne se modifie pas : pour le changer, désactivez-le et préparez-en un nouveau.</p>
      </EtatsLecture>
      {fenetre?.type === 'preparer' && <FenetrePreparerType dispositif={dispositif} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'valider' && <FenetreValiderType type={fenetre.t} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'desactiver' && <FenetreDesactiverType type={fenetre.t} onFermer={() => setFenetre(null)} onFait={fait} />}
    </section>
  );
}
